// src/app/app.module.ts

import { NgModule, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AnimationController, IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { RouteReuseStrategy } from '@angular/router';
import { IonicStorageModule } from '@ionic/storage-angular';
// import { ServiceWorkerModule } from '@angular/service-worker';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component'; // Keep this import
// import { AppInjector } from './services/app-injector.service';

// Native Plugins
// import { ScreenOrientation } from '@awesome-cordova-plugins/screen-orientation/ngx';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { AppVersion } from '@awesome-cordova-plugins/app-version/ngx';
import { CallNumber } from '@awesome-cordova-plugins/call-number/ngx';
import { Network } from '@awesome-cordova-plugins/network/ngx';
import { Camera } from '@awesome-cordova-plugins/camera/ngx';
import { Device } from '@awesome-cordova-plugins/device/ngx';

// import { PageTransitionService } from './services/page-transition.service';
import { RouterStateTrackerService } from './services/router-state-tracker.service';
import { InterceptorService } from './services/interceptor.service';

// --- UPDATED IMPORTS from customAnimation.ts ---
import {
  customAlertEnter,
  customAlertLeave,
  customModalEnter,
  customModalLeave,
  customLoaderEnter,
  customLoaderLeave,
  setGlobalAnimationController,
  setGlobalRouterStateTrackerService,
} from './animations/customAnimation';

import { CommonModule } from '@angular/common';
import { SharedModule } from './shared/shared.module';
import { APP_INITIALIZER } from '@angular/core';
import { ConfigService } from './provider/config.service';

// Factory function for ConfigService initialization
// export function initializeApp(configService: ConfigService) {
//   return () => configService.getAppSettings();
// }
export function initializeConfigService(configService: ConfigService) {
  // configService.loadInitialSettings() is now called in the ConfigService constructor.
  // We just need to ensure the ConfigService instance is created and its constructor runs.
  // The BehaviorSubject in ConfigService will handle the async loading.
  // We can return a Promise that resolves immediately, or if you had a specific
  // async operation in ConfigService that *must* complete before the app starts,
  // you'd await it here. Since loadInitialSettings is async but doesn't need to block
  // the APP_INITIALIZER, we can just return a resolved promise.
  return () => Promise.resolve();
}

// Factory function for TranslateLoader
export function setTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

// --- NEW APP_INITIALIZER factory to set global service references ---
export function initializeGlobalServices(animationCtrl: AnimationController, routerStateTrackerService: RouterStateTrackerService) {
  return () => {
    setGlobalAnimationController(animationCtrl);
    setGlobalRouterStateTrackerService(routerStateTrackerService);
  };
}

@NgModule({
  declarations: [AppComponent], // <<< AppComponent should be declared here.
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    SharedModule,
    CommonModule,
    IonicStorageModule.forRoot(),

    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: setTranslateLoader,
        deps: [HttpClient],
      },
      defaultLanguage: 'en',
    }),
    AppRoutingModule,
    // ServiceWorkerModule.register('ngsw-worker.js', {
    //   enabled: !isDevMode(),
    //   registrationStrategy: 'registerWhenStable:30000',
    // }),
    IonicModule.forRoot({
      mode: 'ios',
      animated: true,
      alertEnter: customAlertEnter,
      alertLeave: customAlertLeave,
      modalEnter: customModalEnter,
      modalLeave: customModalLeave,
      loadingEnter: customLoaderEnter,
      loadingLeave: customLoaderLeave,
      backButtonIcon: 'arrow-back',
      backButtonText: '',
      swipeBackEnabled: true,
    }),
  ],
  providers: [
    // ScreenOrientation,
    Network,
    Device,
    Camera,
    CallNumber,
    AppVersion,
    InAppBrowser,
    AnimationController,
    RouterStateTrackerService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeConfigService,
      deps: [ConfigService], // Make sure ConfigService is injected here
      multi: true, // Essential for multiple APP_INITIALIZERs
    },
    { provide: HTTP_INTERCEPTORS, useClass: InterceptorService, multi: true },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeGlobalServices,
      deps: [AnimationController, RouterStateTrackerService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent], // <<< And bootstrapped here.
  schemas: [],
})
export class AppModule {}
